import React, { useState, useRef } from 'react';
import {
  TextField, Button, IconButton, CircularProgress, Autocomplete, MenuItem, Box, Grid, Paper, Typography,
  FormControl, InputLabel, Select, Chip, FormControlLabel, Checkbox
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const SUBSCRIPTION_PACK_TYPES = [
  "E3 I4", "E2 I4", "E1 I4", "E3 I2", "E2 I2", "E1 I2", "E3 I1", "E2 I1", "E1 I1",
];

const WEEK_DAYS = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
];

const WEEK_OPTIONS = [
  { value: 1, label: '1st Week' },
  { value: 2, label: '2nd Week' },
  { value: 3, label: '3rd Week' },
  { value: 4, label: '4th Week' }
];

function getPackCount(pack) {
  if (!pack) return { exterior: 0, interior: 0 };
  const match = pack.match(/E(\d+)\s*I(\d+)/);
  if (match) {
    return {
      exterior: parseInt(match[1]),
      interior: parseInt(match[2])
    };
  }
  return { exterior: 0, interior: 0 };
}

const AddSubscriptionDialog = ({ open, onClose, onAdded, carId }) => {
  const [step, setStep] = useState(1);
  const [subscriptionPlan, setSubscriptionPlan] = useState('');
  const [subscriptionStartDate, setSubscriptionStartDate] = useState('');
  const [subscriptionExpireDate, setSubscriptionExpireDate] = useState('');
  const [renewMonths, setRenewMonths] = useState(1);
  const [washerLoading, setWasherLoading] = useState(false);
  const [selectedWasher, setSelectedWasher] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);
  const [carQuery, setCarQuery] = useState('');
  const [carList, setCarList] = useState([]);
  const [carLoading, setCarLoading] = useState(false);
  const [washerQuery, setWasherQuery] = useState('');
  const [washerList, setWasherList] = useState([]);
  const [washType, setWashType] = useState('');
  
  // Enhanced schedule state
  const [schedule, setSchedule] = useState([
    { day: '', time: '' },
    { day: '', time: '' },
    { day: '', time: '' }
  ]);
  
  // Interior wash scheduling
  const [interiorSchedule, setInteriorSchedule] = useState({
    type: 'weekly', // 'weekly', 'bi-weekly', 'monthly'
    weeklySchedule: [], // for I2/I1 - which weeks and days
  });
  
  const [loading, setLoading] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    phone: '',
    cars: [{ model: '', name: '', regNo: '' }]
  });
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [price, setPrice] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  
  const debounceRef = useRef();
  const carDebounceRef = useRef();

  const handleStartDateChange = (e) => {
    const value = e.target.value;
    setSubscriptionStartDate(value);
  };

  const handleWasherInputChange = (event, value) => {
    setWasherQuery(value);
    setWasherLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!value) {
        setWasherList([]);
        setWasherLoading(false);
        return;
      }
      const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
      fetch(`${process.env.REACT_APP_SERVER_URI}/washers/options?search=${encodeURIComponent(value)}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      })
        .then(res => res.json())
        .then(result => {
          console.log(result)
          setWasherList(result || []);
          setWasherLoading(false);
        })
        .catch(() => {
          setWasherList([]);
          setWasherLoading(false);
        });
    }, 400);
  };

  const handleCarInputChange = (event, value) => {
    setCarQuery(value);
    setCarLoading(true);
    if (carDebounceRef.current) clearTimeout(carDebounceRef.current);
    carDebounceRef.current = setTimeout(() => {
      if (!value) {
        setCarList([]);
        setCarLoading(false);
        return;
      }
      const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
      fetch(`${process.env.REACT_APP_SERVER_URI}/cars/options?search=${encodeURIComponent(value)}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      })
        .then(res => res.json())
        .then(result => {
          setCarList(result || []);
          setCarLoading(false);
        })
        .catch(() => {
          setCarList([]);
          setCarLoading(false);
        });
    }, 400);
  };

  React.useEffect(() => {
    const counts = getPackCount(subscriptionPlan);
    
    // Reset exterior schedule
    setSchedule([
      { day: '', time: '' },
      { day: '', time: '' },
      { day: '', time: '' }
    ]);
    
    // Initialize interior schedule weeklySchedule for any interior count (1,2,4),
    // so the weekly-schedule UI can render rows for all interior options.
    if (counts.interior >= 1) {
      const type = counts.interior === 4 ? 'weekly' : counts.interior === 2 ? 'bi-weekly' : 'monthly';
      setInteriorSchedule({
        type,
        weeklySchedule: Array.from({ length: counts.interior }, () => ({ week: '', day: '', time: '' }))
      });
    } else {
      setInteriorSchedule({
        type: 'weekly',
        weeklySchedule: []
      });
    }
  }, [subscriptionPlan]);

  const handleScheduleChange = (idx, field, value) => {
    setSchedule(prev =>
      prev.map((item, i) =>
        i === idx ? { ...item, [field]: value } : item
      )
    );
  };

  const handleWeeklyInteriorScheduleChange = (idx, field, value) => {
    setInteriorSchedule(prev => ({
      ...prev,
      weeklySchedule: prev.weeklySchedule.map((item, i) =>
        i === idx ? { ...item, [field]: value } : item
      )
    }));
  };

  const renderInteriorScheduling = () => {
    const counts = getPackCount(subscriptionPlan);
    if (counts.interior === 0) return null;

    const selectedExteriorDays = schedule.slice(0, counts.exterior).map(s => s.day).filter(Boolean);

    if (selectedExteriorDays.length === 0) {
      return (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Please select exterior wash days first to configure interior wash schedule.
          </Typography>
        </Box>
      );
    }

    const labelForInterior = counts.interior === 4 ? 'I4 - Weekly' : counts.interior === 2 ? 'I2 - Bi-weekly' : 'I1 - Monthly';

    return (
      <Box sx={{ mt: 3, p: 2, bgcolor: 'blue.50', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Interior Wash Schedule</Typography>

        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Interior wash schedule ({labelForInterior}):
          </Typography>
          {interiorSchedule.weeklySchedule.map((item, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Week</InputLabel>
                <Select
                  value={item.week}
                  label="Week"
                  onChange={(e) => handleWeeklyInteriorScheduleChange(idx, 'week', e.target.value)}
                >
                  {WEEK_OPTIONS.slice(0, 4).map(week => (
                    <MenuItem key={week.value} value={week.value}>{week.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Day</InputLabel>
                <Select
                  value={item.day}
                  label="Day"
                  onChange={(e) => handleWeeklyInteriorScheduleChange(idx, 'day', e.target.value)}
                >
                  {selectedExteriorDays.map(day => (
                    <MenuItem key={day} value={day}>{day}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  
  const handleSubmit = () => {
    const counts = getPackCount(subscriptionPlan);
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    const expireDateObj = new Date(subscriptionStartDate);
    expireDateObj.setMonth(expireDateObj.getMonth() + Number(renewMonths));
    const finalSchedule = {
      exterior: schedule.slice(0, counts.exterior),
      interior: interiorSchedule
    };
    
    const payload = {
      car: selectedCar ? selectedCar._id : carId,
      washer: selectedWasher ? selectedWasher._id : undefined,
      subscriptionPlan,
      createdBy: 'admin',
      subscriptionStartDate,
      months: renewMonths,
      schedule: finalSchedule,
      price,
      washType,
      // include transactionId only when paid
      ...(isPaid ? { transactionId } : {})
    };
    // for now existing console log / early return preserved
    console.log(payload);
    fetch(`${process.env.REACT_APP_SERVER_URI}/subscriptionGroups/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify(payload),
    })
      .then(res => res.json())
      .then(() => {
        // Reset form
        setSubscriptionPlan('');
        setSubscriptionStartDate('');
        setSubscriptionExpireDate('');
        setWasherQuery('');
        setWasherList([]);
        setSelectedWasher(null);
        setCarQuery('');
        setCarList([]);
        setSelectedCar(null);
        setSchedule([
          { day: '', time: '' },
          { day: '', time: '' },
          { day: '', time: '' }
        ]);
        setInteriorSchedule({
          type: 'weekly',
          weeklySchedule: []
        });
        setPrice('');
        setTransactionId('');
        onClose();
        if (onAdded) onAdded();
      })
      .catch((err) => {
        console.log(err);
        alert('Failed to add subscription.');
      });
  };

  const handleCreateCarInline = (inputValue) => {
    setNewUser({
      name: '',
      email: '',
      phone: '',
      cars: [{ model: '', name: '', regNo: inputValue }]
    });
    setShowAddUserForm(true);
  };

  const handleUserAdded = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setLoading(true);
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    const url = `${process.env.REACT_APP_SERVER_URI}/users/add`;
    const method = 'POST';
    fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify({
        name: newUser.name,
        email: newUser.email,
        phone: newUser.phone,
        cars: newUser.cars
      }),
    })
      .then(res => res.json())
      .then(() => {
        setLoading(false);
        setShowAddUserForm(false);
        alert('User saved successfully.');
      })
      .catch(() => {
        setLoading(false);
        alert('Failed to save user.');
      });
    setNewUser({
      name: '',
      email: '',
      phone: '',
      cars: [{ model: '', name: '', regNo: '' }]
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const handleCarChange = (idx, field, value) => {
    setNewUser(prev => ({
      ...prev,
      cars: prev.cars.map((car, i) =>
        i === idx ? { ...car, [field]: value } : car
      )
    }));
  };

  const handleAddCar = () => {
    setNewUser(prev => ({
      ...prev,
      cars: [...prev.cars, { model: '', name: '', regNo: '' }]
    }));
  };

  const handleRemoveCar = (idx) => {
    setNewUser(prev => ({
      ...prev,
      cars: prev.cars.length > 1 ? prev.cars.filter((_, i) => i !== idx) : prev.cars
    }));
  };

  if (!open) return null;

  const counts = getPackCount(subscriptionPlan);

  return (
    <Paper sx={{ p: 2, width: '100%' }} elevation={1}>
      {step === 1 && (
        <Box>

          <Autocomplete
            freeSolo
            options={carList}
            getOptionLabel={option =>
              option.ownerName
                ? `${option.ownerName} - ${option.name} (${option.regNo})`
                : `${option.name} (${option.regNo})`
            }
            loading={carLoading}
            value={selectedCar}
            onChange={(event, value) => setSelectedCar(value)}
            inputValue={carQuery}
            onInputChange={handleCarInputChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Car (Owner, Name, Reg No)"
                margin="normal"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {carLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography variant="body2">
                  {option.ownerName
                    ? `${option.ownerName} - ${option.name} (${option.regNo})`
                    : `${option.name} (${option.regNo})`}
                </Typography>
              </Box>
            )}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>

            <Select
              value={washType}
              onChange={(e) => setWashType(e.target.value)}
              displayEmpty
              renderValue={(v) => (v ? v : "Select wash type (optional)")}
              MenuProps={{
                PaperProps: { sx: { bgcolor: "#23282e", zIndex: 11000 } },
                MenuListProps: { sx: { bgcolor: "#23282e", zIndex: 11000 } },
              }}
              sx={{
                backgroundColor: "transparent",
                borderRadius: 1,
                border: "1px solid rgba(255,255,255,0.08)",
                ".MuiSelect-select": { paddingTop: "10px", paddingBottom: "10px" },
              }}
            >
              <MenuItem value="waterless" sx={{ color: "white" }}>Waterless car wash</MenuItem>
              <MenuItem value="bucket" sx={{ color: "white" }}>Water Wash (Bucket wash)</MenuItem>
              <MenuItem value="jet-pressure"  sx={{ color: "white" }}>
                Jet Pressure wash
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            select
            label="Subscription Plan"
            value={subscriptionPlan}
            onChange={e => setSubscriptionPlan(e.target.value)}
            fullWidth
            margin="normal"
            required
          >
            {SUBSCRIPTION_PACK_TYPES.map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
          
          {counts.exterior > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Exterior Wash Schedule (E{counts.exterior})
              </Typography>
              {[...Array(counts.exterior)].map((_, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                  <TextField
                    select
                    label={`Day ${idx + 1}`}
                    value={schedule[idx].day}
                    onChange={e => handleScheduleChange(idx, 'day', e.target.value)}
                    sx={{ minWidth: 140 }}
                    size="small"
                  >
                    {WEEK_DAYS.map(day => (
                      <MenuItem key={day} value={day}>{day}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label={`Time ${idx + 1}`}
                    type="time"
                    value={schedule[idx].time}
                    onChange={e => handleScheduleChange(idx, 'time', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Box>
              ))}
            </Box>
          )}
          
          {renderInteriorScheduling()}

          <Box display="flex" gap={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => setStep(2)}
              disabled={!subscriptionPlan}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {step === 2 && (
        <Box>
          <TextField
            label="Start Date"
            type="date"
            value={subscriptionStartDate}
            onChange={handleStartDateChange}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            label="No. of Months"
            type="number"
            value={renewMonths}
            onChange={e => setRenewMonths(e.target.value)}
            fullWidth
            margin="normal"
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Price"
            type="number"
            value={price}
            onChange={e => setPrice(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
           <Autocomplete
             freeSolo
             options={washerList}
             getOptionLabel={option => option.name + (option.phone ? ` (${option.phone})` : '')}
             loading={washerLoading}
             value={selectedWasher}
             onChange={(event, value) => setSelectedWasher(value)}
             inputValue={washerQuery}
             onInputChange={handleWasherInputChange}
             renderInput={(params) => (
               <TextField
                 {...params}
                 label="Search Washer by Name or Phone"
                 margin="normal"
                 fullWidth
                 InputProps={{
                   ...params.InputProps,
                   endAdornment: (
                     <>
                       {washerLoading ? <CircularProgress size={20} /> : null}
                       {params.InputProps.endAdornment}
                     </>
                   ),
                 }}
               />
             )}
           />
            <FormControlLabel
            control={
              <Checkbox
                checked={isPaid}
                onChange={e => {
                  const checked = e.target.checked;
                  setIsPaid(checked);
                  if (!checked) setTransactionId('');
                }}
              />
            }
            label="Paid"
          />
          {isPaid && (
            <TextField
              label="Transaction ID"
              value={transactionId}
              onChange={e => setTransactionId(e.target.value)}
              fullWidth
              margin="normal"
              required
            />
          )}
          {showAddUserForm && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Name"
                name="name"
                value={newUser.name}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Email"
                name="email"
                value={newUser.email}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                type="email"
              />
              <TextField
                label="Phone"
                name="phone"
                value={newUser.phone}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {newUser.cars.map((car, idx) => (
                  <React.Fragment key={idx}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Model"
                        value={car.model}
                        onChange={e => handleCarChange(idx, 'model', e.target.value)}
                        fullWidth
                        margin="normal"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Car Name"
                        value={car.name}
                        onChange={e => handleCarChange(idx, 'name', e.target.value)}
                        fullWidth
                        margin="normal"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Reg No"
                        value={car.regNo}
                        onChange={e => handleCarChange(idx, 'regNo', e.target.value)}
                        fullWidth
                        margin="normal"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <IconButton onClick={() => handleRemoveCar(idx)} disabled={newUser.cars.length === 1}>
                        <DeleteIcon />
                      </IconButton>
                      {idx === newUser.cars.length - 1 && (
                        <IconButton onClick={handleAddCar}>
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
              <Button onClick={onClose} disabled={loading}>Cancel</Button>
              <Button onClick={handleUserAdded} variant="contained" color="primary" disabled={loading}>
                {'Add'}
              </Button>
            </Box>
          )}
          <Box display="flex" gap={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={() => setStep(1)}>Back</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary" disabled={showAddUserForm}>
              Add
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default AddSubscriptionDialog;
